import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * The right-hand drawer (spec 5.1): one slot shared by Help, the Seat panel,
 * and the Violations list. Non-modal, so the schedule stays interactive.
 */
export interface DrawerContent {
  title: string;
  subtitle?: string;
  body: ReactNode;
  /** Wider drawer for tables (violations, diff). */
  wide?: boolean;
}

interface DrawerApi {
  content: DrawerContent | null;
  open(content: DrawerContent): void;
  close(): void;
}

const DrawerContext = createContext<DrawerApi | null>(null);

export function DrawerProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<DrawerContent | null>(null);
  const open = useCallback((c: DrawerContent) => setContent(c), []);
  const close = useCallback(() => setContent(null), []);
  const value = useMemo(() => ({ content, open, close }), [content, open, close]);
  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
}

export function useDrawer(): DrawerApi {
  const ctx = useContext(DrawerContext);
  if (!ctx) throw new Error('useDrawer must be used inside DrawerProvider');
  return ctx;
}

export function DrawerPanel() {
  const { content, close } = useDrawer();

  useEffect(() => {
    if (!content) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [content, close]);

  if (!content) return null;
  return (
    <aside
      role="complementary"
      aria-label={content.title}
      className={
        'flex h-full shrink-0 flex-col border-l border-line bg-white shadow-[-8px_0_24px_rgba(32,33,58,0.06)] ' +
        (content.wide ? 'w-[560px]' : 'w-[400px]')
      }
    >
      <header className="flex items-start gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold text-navy">{content.title}</h2>
          {content.subtitle && <p className="mt-0.5 text-xs text-ink-muted">{content.subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={close}
          className="rounded p-1 text-ink-muted hover:bg-band hover:text-ink"
          aria-label="Close panel"
          title="Close (Esc)"
        >
          <X size={18} />
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{content.body}</div>
    </aside>
  );
}
